import {assert, assertAndContinue} from 'Asserts';
import * as ast from 'notebook/tables/excess/AST';
import {getFunction} from 'notebook/tables/excess/functions';
import * as util from 'notebook/tables/excess/Util';
import {coerceToPrimitive, EvaluablePrimitive, flattenValue} from 'notebook/tables/typed-values/DataTypes';
import {
  BooleanTypedValue,
  DatetimeTypedValue,
  FinalArrayTypedValue,
  FinalTypedValue,
  NonArrayTypedValue,
  NumberTypedValue,
  ScientificTypedValue,
  StringTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {assertNever, Optional} from 'util/Types';

import {BaseTypedValueType, TypedValue} from '../TableConstants';
import {Environment} from './Evaluator';
import {
  ArgTypeAssertion,
  CustomArgCoercionFunction,
  EvaluablePrimitiveSpreadsheetFunction,
  FnReturnValueType,
  FunctionParam,
  LookupFieldProcessedArgValue,
  LookupFieldSpreadsheetFunction,
  SpreadsheetFunction,
  TypedValueSpreadsheetFunction,
} from './FunctionConstants';

function getFunctionFromNode(fnNode: ast.FunctionOperator): SpreadsheetFunction {
  const fn = getFunction(fnNode.type);
  if (fn == null) {
    throw new util.FormulaParseError(`Unexpected operator: ${fnNode.type}`);
  }
  return fn;
}

/**
 * Extract the argument values from the node.
 *
 * Each returned value contains the rawValue and the type.
 */
function extractNodeArgs(
  fnNode: ast.FunctionOperator,
  env?: Environment
): {
  typedValues: Array<TypedValue | null | util.CellError>;
  primitives: Array<EvaluablePrimitive | util.CellError>;
} {
  const functionName = fnNode.type;
  const argNodes = fnNode.children;

  const fn = getFunctionFromNode(fnNode);

  const typedValues: Array<TypedValue | null | util.CellError> = [];
  const primitives: Array<EvaluablePrimitive | util.CellError> = [];

  function validateAndEvaluate(
    type: ArgTypeAssertion,
    optional: boolean,
    node: ast.ASTNode | null,
    pos: number,
    coerce: CustomArgCoercionFunction | null
  ): void {
    let value: TypedValue | null | util.CellError;
    let primitive: EvaluablePrimitive | util.CellError;
    // there may not be a defined function param for a given argument, so force the typing to be potentially undefined.
    const argument: FunctionParam | undefined = fn.params[pos];
    if (node == null) {
      if (!optional) {
        throw new util.FormulaParseError(`Wrong number of arguments to ${functionName}.`);
      }
      value = null;
      primitive = null;
    } else {
      if (env == null) {
        // If env is unknown only plain ast.Value arguments are supported
        if (!(node instanceof ast.Value)) {
          throw new util.FormulaParseError(`Invalid function arguments to ${functionName}.`);
        }
        value = node.eval();
      } else {
        try {
          value = node.eval(env);
          if (!argument?.allowError) {
            util.throwOnErrorTypedValue(value);
          }
        } catch (e) {
          if (e instanceof util.CellError) {
            value = e;
          } else {
            throw e;
          }
        }
      }
      primitive =
        value instanceof util.CellError
          ? value
          : coerce
            ? coerce(value, env?.getLanguageTag())
            : coerceToPrimitive(value, {
                shouldCoerceStringToNumber: true,
                languageTag: env?.getLanguageTag(),
              });
      type(node, primitive, `Function ${functionName} parameter ${pos + 1}`);
    }
    typedValues.push(value);
    primitives.push(primitive);
  }

  // Non-repeated args
  let argPos = 0;
  for (; argPos < fn.params.length; argPos++) {
    const {type, optional, coerce} = fn.params[argPos];
    const argNode = argNodes[argPos] ?? null;
    validateAndEvaluate(type, Boolean(optional), argNode, argPos, coerce ?? null);
  }

  // Repeated args
  if (fn.repeatableLastParamType != null) {
    const coerce = fn.params[fn.params.length - 1]?.coerce ?? null; // repeatable coerce
    for (; argPos < argNodes.length; argPos++) {
      const argNode = argNodes[argPos];
      validateAndEvaluate(fn.repeatableLastParamType, false, argNode, argPos, coerce);
    }
  } else if (argNodes.length > fn.params.length) {
    throw new util.FormulaParseError(`Wrong number of arguments to ${functionName}.`);
  }

  return {typedValues, primitives};
}

/**
 * Extract the type of the first argValue, where the first argValue is flattened if it is an array.
 * If the array is empty, null will be returned.
 *
 * @return {string?}
 */
function extractFirstType(
  argValues: Array<FinalTypedValue | util.CellError>
): FinalTypedValue['type'] | null {
  if (argValues.length === 0) {
    return null;
  } else if (argValues[0].type === util.VALUE_TYPES.ARRAY) {
    return extractFirstType((argValues[0] as FinalArrayTypedValue).rawValue);
  } else if (argValues[0] instanceof util.CellError) {
    throw argValues[0];
  } else {
    return argValues[0].type;
  }
}

export function _getInferredValueTypeFromFnReturnValue(
  fn: SpreadsheetFunction,
  argValues: Array<TypedValue | util.CellError | null>,
  evaluatedRawValue: EvaluablePrimitive
): FnReturnValueType {
  // Functionally fn.returnType only provides the ability to override the automatic inference of NUMBER vs
  // SCIENTIFIC via arg types (as seen in the below if-block), e.g `=COUNT(2.1e3, "hi")` returns 1, not 1e0.
  if (fn.returnType != null) {
    return fn.returnType;
  }
  const inferredType = _inferValueType(evaluatedRawValue);
  if (inferredType === util.ValueType.NUMBER) {
    // If the first argument is SCIENTIFIC, enhance the return type to be SCIENTIFIC, too, e.g.
    // - `=SUM(1, 2e0)` is 3 (NUMBER)
    // - `=SUM(1e0, 2)` is 3e0 (SCIENTIFIC)
    const firstType = extractFirstType(argValues as Array<FinalTypedValue | util.CellError>);
    switch (firstType) {
      case util.ValueType.SCIENTIFIC:
        return util.ValueType.SCIENTIFIC;

      case BaseTypedValueType.DATE:
      case BaseTypedValueType.DATETIME:
      case FieldType.DATE:
      case FieldType.DATETIME:
        return util.ValueType.DATETIME;

      default:
        break;
    }
  }
  return inferredType;
}

export function _inferValueType(evaluatedRawValue: EvaluablePrimitive): FnReturnValueType {
  if (typeof evaluatedRawValue === 'string' || evaluatedRawValue == null) {
    return util.ValueType.STRING;
  } else if (typeof evaluatedRawValue === 'number') {
    return util.ValueType.NUMBER;
  } else if (typeof evaluatedRawValue === 'boolean') {
    return util.ValueType.BOOLEAN;
  } else {
    // oxlint-disable-next-line no-unused-expressions
    evaluatedRawValue satisfies Array<boolean> | Array<string> | Array<number>;
    assert(Array.isArray(evaluatedRawValue), `Unexpected evaluatedRawValue:`, {evaluatedRawValue});
    return util.ValueType.ARRAY;
  }
}

function _assertType(
  cond: boolean,
  {returnType, evaluatedRawValue}: {returnType: EvaluablePrimitive; evaluatedRawValue: EvaluablePrimitive}
): asserts cond {
  assert(cond, `returnType of "${returnType}" is incompat with value of type "${typeof evaluatedRawValue}"`, {
    evaluatedRawValue,
  });
}

export function _makeTypedValueFromPrimitive(
  evaluatedRawValue: EvaluablePrimitive,
  returnType: FnReturnValueType,
  defaultScientificPrecision: Optional<number>
): TypedValue {
  switch (returnType) {
    case util.VALUE_TYPES.NUMBER:
      _assertType(typeof evaluatedRawValue === 'number', {returnType, evaluatedRawValue});
      return {
        rawValue: evaluatedRawValue,
        type: returnType,
        defaultPrecision: null,
      } satisfies NumberTypedValue;
    case util.VALUE_TYPES.SCIENTIFIC:
      _assertType(typeof evaluatedRawValue === 'number', {returnType, evaluatedRawValue});
      return {
        rawValue: evaluatedRawValue,
        type: 'SCIENTIFIC',
        defaultPrecision: defaultScientificPrecision,
      } satisfies ScientificTypedValue;
    case util.VALUE_TYPES.STRING:
      _assertType(typeof evaluatedRawValue === 'string' || evaluatedRawValue == null, {
        returnType,
        evaluatedRawValue,
      });
      return {
        rawValue: evaluatedRawValue ?? '',
        type: returnType,
      } satisfies StringTypedValue;
    case util.VALUE_TYPES.BOOLEAN:
      _assertType(typeof evaluatedRawValue === 'boolean', {returnType, evaluatedRawValue});
      return {
        booleanValue: evaluatedRawValue,
        type: 'BOOLEAN',
      } satisfies BooleanTypedValue;
    case util.VALUE_TYPES.DATETIME: {
      _assertType(typeof evaluatedRawValue === 'number', {returnType, evaluatedRawValue});
      return {
        value: util.daysSinceEpochToDatetimeString(evaluatedRawValue),
        type: returnType,
      } satisfies DatetimeTypedValue;
    }
    case util.VALUE_TYPES.ARRAY: {
      _assertType(Array.isArray(evaluatedRawValue), {returnType, evaluatedRawValue});
      const values: Array<string | number | boolean> = evaluatedRawValue;
      return {
        rawValue: values.map(
          (value) =>
            _makeTypedValueFromPrimitive(
              value,
              _inferValueType(value),
              defaultScientificPrecision
            ) as NonArrayTypedValue
        ),
        type: BaseTypedValueType.ARRAY,
      } satisfies FinalArrayTypedValue;
    }
    default:
      assertNever(returnType);
      throw new Error(`Unexpected type "${returnType}"`);
  }
}

export function _getEvaluatedTypedValue(
  fn: SpreadsheetFunction,
  argValues: Array<TypedValue | util.CellError | null>,
  evaluatedRawValue: EvaluablePrimitive
): TypedValue {
  const returnType = _getInferredValueTypeFromFnReturnValue(fn, argValues, evaluatedRawValue);
  const argTypedValues: Array<TypedValue> = [];
  for (const argValue of argValues) {
    if (argValue && !(argValue instanceof util.CellError)) {
      argTypedValues.push(argValue);
    }
  }
  const defaultScientificPrecision = ast.getPrecisionIfScientific(argTypedValues);
  return _makeTypedValueFromPrimitive(evaluatedRawValue, returnType, defaultScientificPrecision);
}

export function evalFunction(fnNode: ast.FunctionOperator, env: Environment): TypedValue {
  const fn = getFunctionFromNode(fnNode);
  const {typedValues, primitives} = extractNodeArgs(fnNode, env);

  // Call the function with the evaluated args.
  let returnTypedValue: TypedValue;
  if (isTypedValueSpreadsheetFunction(fn)) {
    returnTypedValue = fn.evalWithTypedValues(...typedValues);
  } else {
    let processedValues;
    if (fn.processArgValues) {
      assert(
        typedValues.every((typedValue) => !(typedValue instanceof util.CellError)),
        'Functions that use processArgValues must not accept errors as arguments'
      );
      processedValues = fn.processArgValues(...(typedValues as Array<TypedValue | null>));
    } else {
      processedValues = primitives;
    }
    if (isLookupFieldSpreadsheetFunction(fn)) {
      const [lookupSpecifier] = processedValues as [LookupFieldProcessedArgValue];
      returnTypedValue = fn.evalWithEnv(env, lookupSpecifier);
    } else {
      // oxlint-disable-next-line no-unused-expressions
      fn satisfies EvaluablePrimitiveSpreadsheetFunction;
      const evaluatedRawValue = fn.eval(...(processedValues as Array<EvaluablePrimitive | undefined>));
      assert(
        !(evaluatedRawValue instanceof util.CellError),
        `Function ${fn.name} returned an error value; it should throw instead`
      );
      returnTypedValue = _getEvaluatedTypedValue(fn, typedValues, evaluatedRawValue);
    }
  }

  // TODO(BNCH-115427): returnTypedValue is undefined in some cases (per Sentry)
  assertAndContinue(returnTypedValue != null, `${fn.name} failed to return a typed value`);
  // if the result of the function is an error, throw it.
  util.throwOnErrorTypedValue(returnTypedValue);
  return util.astValue(flattenValue(returnTypedValue));
}

function isLookupFieldSpreadsheetFunction(fn: SpreadsheetFunction): fn is LookupFieldSpreadsheetFunction {
  return (fn as LookupFieldSpreadsheetFunction).evalWithEnv != null;
}

function isTypedValueSpreadsheetFunction(fn: SpreadsheetFunction): fn is TypedValueSpreadsheetFunction {
  return (fn as TypedValueSpreadsheetFunction).evalWithTypedValues != null;
}
